import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useReducer,
  ReactElement,
} from 'react';

import useDetailSearchColumns from './useDetailSearchColumns';

import { HighlightTextWrapper } from 'ui/patterns';

import { useGetAdvancedDetailSearch } from 'hooks';

import { showErrorToast } from 'utils/showToast';
import {
  detailsColumnData,
  searchItem,
  sortItem,
  detailInitialState,
} from 'utils/advanceSearchUtils';

import {
  DetailSearchParams,
  SearchRuleItem,
  DetailOptionsType,
} from '../../advancedSearchTypes';

import useAdvancedSearchStore from 'store/useAdvancedSearchStore';

function reducer(
  state: DetailOptionsType,
  action: {
    type: keyof DetailOptionsType | 'reset';
    newState?: DetailOptionsType;
  },
) {
  switch (action.type) {
    case 'questionDetails': {
      return {
        ...state,
        questionDetails: !state.questionDetails,
      };
    }
    case 'fieldLabel': {
      return {
        ...state,
        fieldLabel: !state.fieldLabel,
      };
    }
    case 'includeHidden': {
      return {
        ...state,
        includeHidden: !state.includeHidden,
      };
    }
    case 'reset':
      if (action.newState) return { ...action.newState };
      return { ...detailInitialState };
    default:
      throw new Error();
  }
}
const initialSearchList: Array<SearchRuleItem> = [{ ...searchItem }];

export default function useDetailSearch() {
  // search block states
  const [searchList, setSearchList] = useState<Array<SearchRuleItem>>([
    ...initialSearchList.map(item => item),
  ]);
  const [searchParams, setSearchParams] = useState<DetailSearchParams>();
  const [columnsSearch, setColumnsSearch] = useState({}); // header search
  const [currentSortItem, setCurrentSortItem] = useState(sortItem);

  //checkbox states
  const [detailsOptions, setDetailsOptions] = useReducer(reducer, {
    ...detailInitialState,
  });

  const { searchStore, setParams } = useAdvancedSearchStore();

  useEffect(() => {
    if (searchStore) {
      if (searchStore.detailSearchParams) {
        const { questionDetails, fieldLabel, searchList, includeHidden } =
          searchStore.detailSearchParams;
        setDetailsOptions({
          type: 'reset',
          newState: {
            questionDetails,
            fieldLabel,
            includeHidden,
          },
        });
        setSearchList(searchList);
      }
      if (searchStore.sortItem) {
        setCurrentSortItem(searchStore.sortItem);
      }
      if (searchStore.columnSearch) {
        setColumnsSearch(searchStore.columnSearch);
      }
      setSearchParams(searchStore.detailSearchParams);
    }
  }, [searchStore]);

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading: advancedSearchloading,
  } = useGetAdvancedDetailSearch(searchParams, columnsSearch, currentSortItem);

  const tableData = useMemo(() => {
    if (data) {
      return data.pages
        .map((dataItem: any) => dataItem?.Data?.Data)
        .flat()
        .filter((item: any) => !!item);
    }
    return;
  }, [data]);

  const currentRecords = useMemo(() => {
    if (data) {
      return data.pages.map((dataItem: any) => dataItem?.Data?.Data)?.flat();
    }
  }, [data]);

  const totalRecords = useMemo(() => {
    if (data) {
      return data.pages[0] && data.pages[0]?.Data?.TotalRecords;
    }
    return 0;
  }, [data]);

  const csvImportData = tableData?.length ? tableData : [];

  const detailsDataCsv = [
    [
      'CID',
      'PID',
      'SID',
      'AccountName',
      'FieldName',
      'FieldLabel',
      'RelationshipManagerName',
    ],
    ...csvImportData.map(
      ({
        CID,
        PID,
        SID,
        AccountName,
        FieldName,
        FieldLabel,
        RelationshipManagerName,
      }: any) => [
        CID,
        PID,
        SID,
        AccountName,
        FieldName,
        FieldLabel,
        RelationshipManagerName,
      ],
    ),
  ];

  const onEndReached = () => {
    if (hasNextPage) fetchNextPage();
  };

  const columnList = useDetailSearchColumns([...detailsColumnData.slice()]);

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch],
  );

  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (
        currentSortItem.sortKey === item &&
        currentSortItem.sortOrder === 'asc'
      ) {
        sortOrder = 'desc';
      }
      setCurrentSortItem({
        sortKey: item,
        sortOrder,
      });
    },
    [setCurrentSortItem, currentSortItem],
  );

  const resetDetailSearch = () => {
    setSearchList([{ ...searchItem }]);
    setColumnsSearch({});
    setCurrentSortItem(sortItem);
    setDetailsOptions({ type: 'reset' });
    setSearchParams(undefined);
  };

  const toggleDetailOption = (key?: keyof DetailOptionsType) => () => {
    if (!key) return setDetailsOptions({ type: 'reset' });
    return setDetailsOptions({ type: key });
  };

  function searchButtonClick() {
    if (searchList.filter(item => item.value.trim().length === 0).length > 0) {
      showErrorToast({
        message: 'Please Enter a keyword',
      });
    } else {
      setSearchParams({
        searchList: JSON.parse(JSON.stringify(searchList.slice())),
        ...detailsOptions,
      });
    }
  }

  function onClickToolTip(
    path: string,
    params: Record<string, number | string>,
  ) {
    setParams({
      detailSearchParams: searchParams,
      columnSearch: columnsSearch,
      sortItem: currentSortItem,
    });
    localStorage.setItem('fromSearch', JSON.stringify(params?.Pid));
    window.open(path);
  }

  function getHighlightedText(content: string): ReactElement {
    let newString = [content];
    searchList?.forEach(searchItem => {
      const arr: string[] = [];
      newString?.forEach(x => {
        if (x !== undefined)
          arr?.push(...x?.split(new RegExp(`(${searchItem.value})`, 'gi')));
      });
      newString = [...arr];
    });

    return (
      <>
        <HighlightTextWrapper content={content} searchedWords={searchList} />
      </>
    );
  }

  return {
    detailsDataCsv,
    totalDetailRecordsCount: totalRecords,
    listedDetailRecordCount: currentRecords,
    isDetailSearchFetching: isFetching,
    detailsOptions,
    toggleDetailOption,
    resetDetailSearch,
    detailTableProps: {
      columns: columnList,
      columnsSearch: columnsSearch,
      data: tableData,
      headerUpdateCallBack: headerUpdateCallBack,
      sortedItem: currentSortItem,
      handleSortChange: handleSortChange,
      isLoading: advancedSearchloading,
      noOptions: true,
      onEndReached: onEndReached,
      isFetching: !!isFetchingNextPage,
      onRedirect: onClickToolTip,
      getHighlightedText,
    },
    detailSearch: {
      searchList,
      setSearchList,
      searchButtonClick,
    },
  };
}
